import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import {
  View,
  FlatList,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import Urls from '../../services/Urls';
import ajax from '../../helpers/ajaxHelper';
import drawerHoc from '../../components/drawerHOC';
import { navigate } from '../../navigation/RootNavigation';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';
import _ from 'lodash';

/* Reminder Type Tag Badge matching Figma */
const ReminderTypeTag = ({ type }) => {
  const t = (type || '').toLowerCase();
  let label = 'Annual';
  if (t === 'auto') label = 'Auto';
  else if (t === 'manual') label = 'Manual';
  else if (type) label = type;

  return (
    <View style={tagStyles.wrap}>
      <Text style={tagStyles.txt}>{label}</Text>
    </View>
  );
};
const tagStyles = StyleSheet.create({
  wrap: {
    backgroundColor: '#FEF3C7',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
    alignSelf: 'flex-start',
  },
  txt: {
    color: '#D97706',
    fontSize: 11,
    fontWeight: '600',
  },
});

const Reminder = () => {
  const [reminders, setReminders] = useState([]);
  const [filteredReminders, setFilteredReminders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const focus = useIsFocused();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  useEffect(() => {
    ajax
      .get(`${Urls.ADD_REMAINDERS}?filterReminders=true`)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setReminders(data?.payload);
          setFilteredReminders(data?.payload);
        }
      })
      .catch((error) => console.log('error reminders', error));
  }, [focus]);

  const handleSearch = (text) => {
    setSearchQuery(text);
    if (!text || !text.trim()) {
      setFilteredReminders(reminders);
      return;
    }
    const q = text.toLowerCase();
    const filtered = reminders.filter((item) => {
      const title = (item?.title || '').toLowerCase();
      const propName = (item?.property?.name || '').toLowerCase();
      const postcode = (item?.property?.postcode || '').toLowerCase();
      return title.includes(q) || propName.includes(q) || postcode.includes(q);
    });
    setFilteredReminders(filtered);
  };

  const handleItemPress = (item) => {
    if (item.type === 'Outgoing') {
      navigate('PaymentOutgoingDetails', { id: item?.id, type: 'EDIT' });
    } else if (item.type === 'Incoming') {
      navigate('PaymentIncomingDetails', { id: item?.id, type: 'EDIT' });
    } else {
      navigate('EditReminder', { id: item.id, type: item.type });
    }
  };

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Page Header */}
      <Text style={styles.breadcrumbText}>Back / Reminders</Text>

      <View style={[styles.headerRow, isMobile && styles.headerRowMobile]}>
        <View style={{ flex: 1 }}>
          <Text style={styles.pageTitle}>My Reminders</Text>
          <Text style={styles.pageSubtitle}>Stay on top of your property tasks and deadlines.</Text>
        </View>

        <TouchableOpacity
          style={styles.addBtn}
          onPress={() => navigation.push('AddReminder', { type: 'other' })}
          activeOpacity={0.85}
        >
          <Ionicons name="add" size={16} color="#FFFFFF" style={{ marginRight: 4 }} />
          <Text style={styles.addBtnText}>Add Reminder</Text>
        </TouchableOpacity>
      </View>

      {/* Main White Card Container matching Figma */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        {/* Search Bar */}
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
          <TextInput
            placeholder="Search reminders..."
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={handleSearch}
            style={styles.searchInput}
          />
        </View>

        {/* Data Table / List */}
        {filteredReminders && filteredReminders.length > 0 ? (
          <View style={styles.tableWrapper}>
            {!isMobile ? (
              <View style={styles.tableHeader}>
                <Text style={[styles.thText, { flex: 2 }]}>TITLE</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>TYPE</Text>
                <Text style={[styles.thText, { flex: 2 }]}>PROPERTY</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>DATE</Text>
                <Text style={[styles.thText, { width: 60, textAlign: 'right' }]}>ACTIONS</Text>
              </View>
            ) : null}

            <FlatList
              data={filteredReminders}
              keyExtractor={(item, index) => item?.id?.toString() || index.toString()}
              renderItem={({ item }) => {
                const titleStr = item?.title || 'Gas Certificate';
                const typeStr = item?.added_by === 'auto' ? 'Auto' : 'Annual';
                const propStr = item?.property?.name || 'All Properties';
                const dateStr = item?.date ? moment(item.date).format('DD/MM/YYYY') : '09/11/2027';

                return !isMobile ? (
                  /* WEB ROW */
                  <TouchableOpacity
                    style={styles.tableRow}
                    onPress={() => handleItemPress(item)}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.tdBold, { flex: 2 }]} numberOfLines={1}>{titleStr}</Text>
                    <View style={{ flex: 1.2 }}>
                      <ReminderTypeTag type={typeStr} />
                    </View>
                    <Text style={[styles.tdText, { flex: 2 }]} numberOfLines={1}>{propStr}</Text>
                    <Text style={[styles.tdText, { flex: 1.2 }]}>{dateStr}</Text>
                    <TouchableOpacity
                      style={styles.actionBtn}
                      onPress={() => handleItemPress(item)}
                      activeOpacity={0.8}
                    >
                      <Feather name="edit-2" size={16} color="#6B7280" />
                    </TouchableOpacity>
                  </TouchableOpacity>
                ) : (
                  /* MOBILE CARD */
                  <TouchableOpacity
                    style={styles.mobileCard}
                    onPress={() => handleItemPress(item)}
                    activeOpacity={0.85}
                  >
                    <View style={styles.mobileTopRow}>
                      <ReminderTypeTag type={typeStr} />
                      <TouchableOpacity onPress={() => handleItemPress(item)} activeOpacity={0.7}>
                        <Feather name="edit-2" size={16} color="#6B7280" />
                      </TouchableOpacity>
                    </View>

                    <Text style={styles.mobileCardTitle}>{titleStr}</Text>
                    <Text style={styles.mobilePropSubtitle}>{propStr}</Text>

                    <View style={styles.mobileFooterRow}>
                      <Text style={styles.mobileDateVal}>📅 {dateStr}</Text>
                      <Text style={styles.mobileViewDetailsText}>View Details</Text>
                    </View>
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        ) : (
          /* Empty / Default Demo Rows matching Figma */
          <View style={styles.tableWrapper}>
            {!isMobile ? (
              <View style={styles.tableHeader}>
                <Text style={[styles.thText, { flex: 2 }]}>TITLE</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>TYPE</Text>
                <Text style={[styles.thText, { flex: 2 }]}>PROPERTY</Text>
                <Text style={[styles.thText, { flex: 1.2 }]}>DATE</Text>
                <Text style={[styles.thText, { width: 60, textAlign: 'right' }]}>ACTIONS</Text>
              </View>
            ) : null}

            {/* Demo Row 1 */}
            {!isMobile ? (
              <View style={styles.tableRow}>
                <Text style={[styles.tdBold, { flex: 2 }]}>Gas Certificate</Text>
                <View style={{ flex: 1.2 }}>
                  <ReminderTypeTag type="Annual" />
                </View>
                <Text style={[styles.tdText, { flex: 2 }]}>All Properties</Text>
                <Text style={[styles.tdText, { flex: 1.2 }]}>09/11/2027</Text>
                <TouchableOpacity style={styles.actionBtn} activeOpacity={0.8}>
                  <Feather name="edit-2" size={16} color="#6B7280" />
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.mobileCard}>
                <View style={styles.mobileTopRow}>
                  <ReminderTypeTag type="Annual" />
                  <Feather name="edit-2" size={16} color="#6B7280" />
                </View>
                <Text style={styles.mobileCardTitle}>Gas Certificate</Text>
                <Text style={styles.mobilePropSubtitle}>All Properties</Text>
                <View style={styles.mobileFooterRow}>
                  <Text style={styles.mobileDateVal}>📅 09/11/2027</Text>
                  <Text style={styles.mobileViewDetailsText}>View Details</Text>
                </View>
              </View>
            )}

            {/* Demo Row 2 */}
            {!isMobile ? (
              <View style={styles.tableRow}>
                <Text style={[styles.tdBold, { flex: 2 }]}>Mortgage</Text>
                <View style={{ flex: 1.2 }}>
                  <ReminderTypeTag type="Annual" />
                </View>
                <Text style={[styles.tdText, { flex: 2 }]}>4 Lawlors</Text>
                <Text style={[styles.tdText, { flex: 1.2 }]}>22/06/2026</Text>
                <TouchableOpacity style={styles.actionBtn} activeOpacity={0.8}>
                  <Feather name="edit-2" size={16} color="#6B7280" />
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.mobileCard}>
                <View style={styles.mobileTopRow}>
                  <ReminderTypeTag type="Annual" />
                  <Feather name="edit-2" size={16} color="#6B7280" />
                </View>
                <Text style={styles.mobileCardTitle}>Mortgage</Text>
                <Text style={styles.mobilePropSubtitle}>4 Lawlors</Text>
                <View style={styles.mobileFooterRow}>
                  <Text style={styles.mobileDateVal}>📅 22/06/2026</Text>
                  <Text style={styles.mobileViewDetailsText}>View Details</Text>
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

export default drawerHoc({ Component: Reminder });

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  headerRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 2,
  },

  addBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 18,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },

  /* MAIN CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 20,
    ...SHADOWS.sm,
  },
  cardContainerMobile: {
    padding: 14,
    borderRadius: BORDER_RADIUS.md,
  },

  /* SEARCH BOX */
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 40,
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  /* TABLE */
  tableWrapper: {
    marginTop: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
    marginBottom: 4,
  },
  thText: {
    fontSize: 11,
    fontWeight: '700',
    color: COLORS.textMuted,
    letterSpacing: 0.5,
  },

  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderColor: '#F9FAFB',
  },
  tdBold: {
    fontSize: 14,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  tdText: {
    fontSize: 13,
    color: COLORS.textSecondary,
  },

  actionBtn: {
    width: 60,
    alignItems: 'flex-end',
  },

  /* MOBILE CARD */
  mobileCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  mobileTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  mobileCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 2,
  },
  mobilePropSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginBottom: 8,
  },
  mobileFooterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },
  mobileDateVal: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  mobileViewDetailsText: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.primary,
  },
});
